/**
 * Typed API client generated from the backend's OpenAPI schema (`pnpm gen:api`).
 * Adds the CSRF header to changes, turns RFC 9457 problems into `ApiError`, and reports
 * expired sessions so the app can return to sign-in.
 */
import createClient, { type Middleware } from 'openapi-fetch';
import { i18n } from '../lib/i18n';
import { en } from '../lib/i18n/resources/en';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const CSRF_COOKIES = ['__Host-botimir_csrf', 'botimir_csrf'];

export interface FieldError {
  field: string;
  message: string;
  code?: string;
}

type ServerErrorCode = keyof typeof en.serverErrors;

function isKnown(code: string | undefined): code is ServerErrorCode {
  return code !== undefined && Object.hasOwn(en.serverErrors, code);
}

/** The translated text for a server error code, or the server's English message. */
export function translateError(code: string | undefined, message: string): string {
  return isKnown(code) ? i18n.t(`serverErrors.${code}`) : message;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, errors: FieldError[] = []) {
    super(translateError(code, message));
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = Object.fromEntries(
      errors.map((e) => [e.field, translateError(e.code, e.message)]),
    );
  }
}

export function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

function csrfToken(): string | null {
  for (const name of CSRF_COOKIES) {
    const value = readCookie(name);
    if (value) return value;
  }
  return null;
}

let onUnauthorized: () => void = () => undefined;

/** The session entity registers what happens when the server says the session is gone. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

const middleware: Middleware = {
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method)) {
      const token = csrfToken();
      if (token) request.headers.set('X-CSRF-Token', token);
    }
    return request;
  },
  onResponse({ response, request }) {
    if (response.status === 401 && !new URL(request.url).pathname.startsWith('/api/v1/auth/')) {
      onUnauthorized();
    }
    return response;
  },
};

export const api = createClient<paths>({ baseUrl: '', credentials: 'same-origin' });
api.use(middleware);

interface Problem {
  detail?: string;
  code?: string;
  errors?: FieldError[];
}

function isProblem(value: unknown): value is Problem {
  return typeof value === 'object' && value !== null && ('detail' in value || 'code' in value);
}

/** Awaits an openapi-fetch call and returns its data, or throws an `ApiError`. */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch {
    throw new ApiError(0, 'network', 'Network error. Check your connection.');
  }
  const { data, error, response } = result;
  if (!response.ok) {
    const problem = isProblem(error) ? error : {};
    throw new ApiError(
      response.status,
      problem.code ?? 'http',
      problem.detail ?? response.statusText,
      problem.errors ?? [],
    );
  }
  return data as T;
}
